import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Instagram } from "lucide-react";
import { useLang } from "../context/LangContext";
import { api, INSTAGRAM } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Newsletter } from "../components/Newsletter";

const HERO_IMG = "https://images.unsplash.com/photo-1611652022419-a9419f74343d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ABOUT_IMG = "https://images.unsplash.com/photo-1599071338288-49173359e0fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const IG_IMAGES = [
  "https://images.unsplash.com/photo-1721807550979-6e662d370e92?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1629224316810-9d8805b95e76?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1589207212797-cfd546dea0fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1601121141499-17ae80afc03a?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
];

export default function Home() {
  const { t } = useLang();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="min-h-screen grid md:grid-cols-2 items-center pt-24 md:pt-0">
        <div className="px-6 md:pl-16 lg:pl-24 order-2 md:order-1 py-12">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="overline text-brand-primary mb-6"
          >
            {t("hero_overline")}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95] text-ink"
          >
            {t("hero_title")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="text-ink-soft text-lg leading-relaxed mt-6 max-w-md"
          >
            {t("hero_sub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <Link to="/tienda" data-testid="hero-shop-btn" className="group bg-brand-primary text-white px-8 py-4 text-sm uppercase tracking-wide hover:bg-brand-dark transition-colors flex items-center gap-2">
              {t("hero_cta")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/nosotras" data-testid="hero-about-btn" className="border border-ink text-ink px-8 py-4 text-sm uppercase tracking-wide hover:bg-ink hover:text-canvas transition-colors">
              {t("hero_cta2")}
            </Link>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="order-1 md:order-2 h-[50vh] md:h-screen overflow-hidden"
        >
          <img src={HERO_IMG} alt="Pasionaria" className="w-full h-full object-cover" />
        </motion.div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
          <div>
            <p className="overline text-brand-primary mb-3">{t("featured_overline")}</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-ink">{t("featured_title")}</h2>
            <p className="text-ink-soft mt-3 max-w-md">{t("featured_sub")}</p>
          </div>
          <Link to="/tienda" className="text-brand-primary text-sm uppercase tracking-wide hover:underline flex items-center gap-2 self-start">
            {t("view_all")} <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* About */}
      <section className="bg-canvas-soft">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div className="overflow-hidden aspect-[4/5] md:aspect-square">
            <img src={ABOUT_IMG} alt="Artesanas Pasionaria" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="overline text-brand-primary mb-4">{t("about_overline")}</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-ink mb-6">{t("about_title")}</h2>
            <p className="text-ink-soft leading-relaxed mb-4">{t("about_p1")}</p>
            <p className="text-ink-soft leading-relaxed">{t("about_p2")}</p>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />

      {/* Instagram */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <p className="overline text-brand-primary mb-3">{t("instagram_title")}</p>
          <a href={INSTAGRAM} target="_blank" rel="noreferrer" data-testid="home-instagram" className="font-heading text-3xl text-ink hover:text-brand-primary transition-colors inline-flex items-center gap-2">
            <Instagram size={26} strokeWidth={1.5} /> @pasionaria30
          </a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {IG_IMAGES.map((img, i) => (
            <a key={i} href={INSTAGRAM} target="_blank" rel="noreferrer" className="overflow-hidden aspect-square group">
              <img src={img} alt="Instagram" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
